import React, {useEffect, useContext, useState, useRef} from 'react';
import {ViewDataContext} from '../../commons/context/view/ViewDataContext';
import RTSDataUI from "./RTSDataUI";
import {FiltersStateContext} from "../../commons/context/FiltersStateContext";
import {getRefreshInterval, isINRegion} from "../../util/CommonUtils";
import {useDeviceLayout} from "../../commons/context/DeviceLayoutContext";
import {getFeatureStatus, oldTimeZoneMapper} from "../../commons/CommonUtils";
import {FEATURES} from "../../commons/constants/CommonConstants";
import {FILTER_ACTIONS, FILTER_KEYS} from "../../commons/constants/FilterConstants";
import {rtsRowMapping, rtsRowMappingLegacy, rtsRowMappingSimplified} from "../../commons/dataStore/PackageResultsData";
import {RTS_WIDGET_IDS, fetchAllRTSMetrics, getRTSWidgets} from "../../commons/api/RTSWidgetAPI";

const DEFAULT_WIDGET_CONFIGS = RTS_WIDGET_IDS.map(widgetId => ({widgetId}));

function normalizeWidgetConfigs(widgetData) {
    if (!widgetData || !Array.isArray(widgetData.widgets)) {
        return DEFAULT_WIDGET_CONFIGS;
    }

    const configsById = widgetData.widgets.reduce((result, widget) => {
        if (!widget || !RTS_WIDGET_IDS.includes(widget.widgetId)) {
            return result;
        }

        try {
            result[widget.widgetId] = {
                widgetId: widget.widgetId,
                ...(widget.config ? JSON.parse(widget.config) : {})
            };
        } catch (err) {
            console.warn(`Failed to parse widget config for ${widget.widgetId}:`, err);
            result[widget.widgetId] = {widgetId: widget.widgetId};
        }

        return result;
    }, {});

    return DEFAULT_WIDGET_CONFIGS.map(({widgetId}) => configsById[widgetId] || {widgetId});
}

export const RTSData = () => {

    let search = window.location.search;
    let params = new URLSearchParams(search);
    let stationCode = params.get('stationCode');

    const {viewModel, dispatch} = useContext(ViewDataContext)
    //Whenever filter value will change, it should re render
    const {selectedFilterValues, dispatch: dispatchFilter} = useContext(FiltersStateContext)
    const {spacingInset_shorthand2} = useDeviceLayout();
    const [waterfallCleanupEnabled, setWaterfallCleanupEnabled] = useState(false);
    const [simplifiedViewsEnabled, setSimplifiedViewsEnabled] = useState(false);
    const [widgetCardsEnabled, setWidgetCardsEnabled] = useState(false);
    const [widgetMetrics, setWidgetMetrics] = useState({});
    const [widgetConfigs, setWidgetConfigs] = useState([]);
    const [widgetMetricsLoading, setWidgetMetricsLoading] = useState(false);
    const widgetRequestIdRef = useRef(0);

    useEffect(() => {
        let isMounted = true;

        getFeatureStatus(FEATURES.IS_RTS_WATERFALL_CLEANUP_ENABLED)
            .then(enabled => {
                if (isMounted) {
                    setWaterfallCleanupEnabled(!!enabled);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setWaterfallCleanupEnabled(false);
                }
            });
        getFeatureStatus(FEATURES.IS_RTS_SIMPLIFIED_VIEWS_ENABLED)
            .then(enabled => {
                if (isMounted) {
                    setSimplifiedViewsEnabled(!!enabled);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setSimplifiedViewsEnabled(false);
                }
            });
        getFeatureStatus(FEATURES.IS_RTS_METRIC_CARDS_ENABLED)
            .then(enabled => {
                if (isMounted) {
                    setWidgetCardsEnabled(!!enabled);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setWidgetCardsEnabled(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    // The cards only render when BOTH the metric-cards flag and simplified views are ON
    // (RTSDataUI's showComplianceCards requires isSimplifiedView), so both the config
    // fetch and the metrics polling below are gated on this — otherwise a station with
    // metric cards ON but simplified views OFF would poll 4 GetMetricData calls per
    // refresh interval for cards that are never displayed.
    const widgetCardsVisible = widgetCardsEnabled && simplifiedViewsEnabled;

    useEffect(() => {
        if (!widgetCardsVisible) {
            setWidgetConfigs([]);
            setWidgetMetrics({});
            setWidgetMetricsLoading(false);
            return undefined;
        }

        let isMounted = true;
        getRTSWidgets()
            .then(widgetData => {
                if (isMounted) {
                    setWidgetConfigs(normalizeWidgetConfigs(widgetData));
                }
            })
            .catch(err => {
                console.warn("Failed to fetch RTS widget configs:", err);
                if (isMounted) {
                    setWidgetConfigs(DEFAULT_WIDGET_CONFIGS);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [widgetCardsVisible]);

    const rtsFilters = viewModel.stationTimeZone
        ? {...selectedFilterValues, stationTimeZone: viewModel.stationTimeZone}
        : selectedFilterValues;
    const hasPartialEadFilter =
        Boolean(selectedFilterValues.startPDD) !== Boolean(selectedFilterValues.endPDD);

    useEffect(() => {
        if (!widgetCardsVisible || !stationCode) {
            setWidgetMetrics({});
            setWidgetMetricsLoading(false);
            return undefined;
        }

        // The EAD pickers update one bound at a time. Skip the intermediate half-filled
        // state so GetMetricData only sees either a complete range or no EAD filter.
        if (hasPartialEadFilter) {
            setWidgetMetricsLoading(false);
            return undefined;
        }

        let isMounted = true;
        const requestId = ++widgetRequestIdRef.current;

        const fetchMetrics = async () => {
            setWidgetMetricsLoading(true);
            try {
                const results = await fetchAllRTSMetrics(stationCode, rtsFilters);
                if (isMounted && widgetRequestIdRef.current === requestId) {
                    setWidgetMetrics(results);
                }
            } catch (err) {
                console.warn("Failed to fetch RTS widget metrics:", err);
                if (isMounted && widgetRequestIdRef.current === requestId) {
                    setWidgetMetrics({});
                }
            } finally {
                if (isMounted && widgetRequestIdRef.current === requestId) {
                    setWidgetMetricsLoading(false);
                }
            }
        };

        fetchMetrics();
        const interval = setInterval(() => {
            if (!document.hidden) {
                fetchMetrics();
            }
        }, getRefreshInterval());

        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [widgetCardsVisible, stationCode, hasPartialEadFilter, selectedFilterValues.startPDD,
        selectedFilterValues.endPDD, selectedFilterValues.packageTypes_Reverse, viewModel.stationTimeZone]);

    // Calendar-day default UI override — sets startPDD/endPDD to the station-local
    // current calendar day on first mount, so the date pickers reflect the applied
    // range and the API request bounds to today. Runs only ONCE (guarded by ref)
    // so user's subsequent manual date changes stick. Rollback via
    // FEATURES.IS_RTS_CALENDAR_DAY_DEFAULT_ENABLED — when flag OFF, parent-provided
    // context wins and legacy behavior is preserved.
    //
    // Race-condition guard: viewModel.stationTimeZone arrives only after the first
    // API response (initially undefined). Applying "today" against a browser-tz
    // fallback and then setting the guard would freeze the wrong day if the station
    // is in a different tz. We therefore defer until stationTimeZone is populated.
    const calendarDayOverrideAppliedRef = useRef(false);
    useEffect(() => {
        if (calendarDayOverrideAppliedRef.current) return;
        if (!viewModel.stationTimeZone) return; // wait for real station tz
        getFeatureStatus(FEATURES.IS_RTS_CALENDAR_DAY_DEFAULT_ENABLED).then(enabled => {
            if (!enabled) return;
            calendarDayOverrideAppliedRef.current = true;
            const tz = oldTimeZoneMapper(viewModel.stationTimeZone);
            // Format today in station-local timezone as YYYY-MM-DD (DatePicker input format)
            const fmt = new Intl.DateTimeFormat("en-CA", {
                timeZone: tz || undefined,
                year: "numeric", month: "2-digit", day: "2-digit"
            });
            const today = fmt.format(new Date()); // en-CA -> YYYY-MM-DD
            // Only dispatch when the value actually changes. Each SELECT_FILTER updates
            // startPDD/endPDD, which are deps of the main data-fetch effect below; blindly
            // re-setting them to a value they already hold would trigger an extra, redundant
            // LOAD_VIEW with identical bounds. Guarding here avoids that duplicate API call.
            if (selectedFilterValues.startPDD !== today) {
                dispatchFilter({type: FILTER_ACTIONS.SELECT_FILTER, key: FILTER_KEYS.START_PDD, value: today});
            }
            if (selectedFilterValues.endPDD !== today) {
                dispatchFilter({type: FILTER_ACTIONS.SELECT_FILTER, key: FILTER_KEYS.END_PDD, value: today});
            }
        }).catch(err => {
            // Flag lookup failed (e.g. BosonAppConfig timeout). The catch is required
            // so the rejected promise doesn't surface as an unhandled rejection; we log
            // and intentionally take no action — the calendar-day default is simply not
            // applied and the parent-provided filter context stays in effect (safe default).
            console.warn("RTS calendar-day default: feature-flag lookup failed, keeping parent filter context", err);
        });
    }, [viewModel.stationTimeZone, dispatchFilter]);

    const rowMapping = simplifiedViewsEnabled ? rtsRowMappingSimplified
        : waterfallCleanupEnabled ? rtsRowMapping : rtsRowMappingLegacy;
    // the calendar-day default uses the station's timezone from the API response
    // (viewModel.stationTimeZone). It is absent on the first paint (before any response),
    // so FilterConverter falls back to the browser runtime tz until it arrives; the dep
    // array below re-fires the request once stationTimeZone is known.
    useEffect(() => {

        constructState(selectedFilterValues);
        dispatch({ type: 'LOAD_VIEW', data:{filters: rtsFilters , stationCode, view : "RTS_VIEW" ,dispatcher: dispatch, leg:"REVERSE", isCPTFilter: false}});
        const interval = setInterval(() => {

            constructState(selectedFilterValues);
                        //check for inactive tabs
                        if ( !document.hidden)
                        {
                            dispatch({ type: 'LOAD_VIEW', data:{filters: rtsFilters , stationCode, view : "RTS_VIEW" ,dispatcher: dispatch, leg:"REVERSE", isCPTFilter: false}});
                        }
    }, getRefreshInterval());
    return () => clearInterval(interval);}, isINRegion() ? [selectedFilterValues.startPDD, selectedFilterValues.endPDD, selectedFilterValues.packageTypes_Reverse, selectedFilterValues.shipDateStart, selectedFilterValues.shipDateEnd, viewModel.stationTimeZone] : [selectedFilterValues.startPDD, selectedFilterValues.endPDD, selectedFilterValues.packageTypes_Reverse, viewModel.stationTimeZone]);

    return (
        <RTSDataUI data = {viewModel.metricResult} isLoading = {viewModel.isLoading} isRtsAgeingGenericLabelsEnabled = {viewModel.rtsAgeingGenericLabelsEnabled}
             appliedFilterURL = { selectedFilterValues.appliedFilterURL} spacingInset_shorthand2={spacingInset_shorthand2}
             rowMapping={rowMapping} renderTooltips={waterfallCleanupEnabled} isSimplifiedView={simplifiedViewsEnabled}
             widgetCardsEnabled={widgetCardsEnabled} widgetMetrics={widgetMetrics} widgetConfigs={widgetConfigs} widgetMetricsLoading={widgetMetricsLoading}/>
    )

}

const constructState = (selectedFilterValues) => {
    var values = {

        startPDD : selectedFilterValues.startPDD,
        endPDD : selectedFilterValues.endPDD,
        packageTypes_Reverse : selectedFilterValues.packageTypes_Reverse

    }

    //Redirect to search path
    var path = window.location.href.split("rtsBoard")[0];
    return window.location.replace( path + "rtsBoard/"+ JSON.stringify(values).replace('/','%2f'));
}



// WEBPACK FOOTER //
// ./src/components/rts/RTSData.js